import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';

import { useDeepCompareMemo } from '@clatter/platform';
import { addNotification } from '../../../../store';
import { projectPagesMap } from '../../../../constants';
import { useUpdateProjectTemplateMutation } from '../../../../services';
import { useGetCCMSTypesQuery } from '../../../../services/ccms.service';

import { FolderManagerDynamicForm } from '../../../FolderManager';
import { NgBreadcrumbs, NgCard, NgCardContent, NgCardHeader } from '../../../UI';
import { NgAssetUploadInput } from '../../../NgAssetUploadInput/NgAssetUploadInput';

interface Props {
  workspaceData: any;
  isLoading: boolean;
}

const formSchema = {
  type: 'object',
  properties: {
    enableDataBrowser: {
      type: 'boolean',
      title: 'Enable Data Browser',
    },
    dataBrowserSourceSheet: {
      type: 'object',
      title: 'Data Browser Source Sheet',
    },
    thumbnail: {
      type: 'object',
      title: 'Thumbnail',
    },
    //@todo schema validation
  },
};

const breadcrumbs = [
  {
    id: 1,
    slug: projectPagesMap.WORKSPACE_SETTINGS.slug,
    name: projectPagesMap.WORKSPACE_SETTINGS.displayName,
  },
];

const allOption = { value: 'All', label: 'All' };

export const WorkspaceSettingsPage: FC<Props> = ({ workspaceData, isLoading: isParentLoading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [updateProjectTemplate, { isLoading: isUpdateProjectTemplateLoading }] = useUpdateProjectTemplateMutation();

  const { data: typesData, isFetching: isTypesDataLoading } = useGetCCMSTypesQuery(
    { url: `/cms-scopes/${workspaceData?.CmsScopeID}/types` },
    {
      skip: !workspaceData?.CmsScopeID,
      refetchOnMountOrArgChange: true,
    },
  );

  const isLoading = isParentLoading || isUpdateProjectTemplateLoading || isTypesDataLoading;

  const customFormMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(formSchema as any, {
      formats: fullFormats,
    }),
  });

  const {
    watch,
    handleSubmit,
    control,
    formState: { isDirty, isValid, touchedFields, dirtyFields },
  } = customFormMethods;

  const thumbnail = watch('thumbnail');

  const enableDataBrowser = watch('enableDataBrowser');

  const fields = useDeepCompareMemo(() => {
    return [
      {
        name: 'enableDataBrowser',
        inputType: 'checkbox',
        label: 'Enable Data Browser',
        inputOptions: {
          order: 1,
          hideLabel: true,
        },
      },
      { name: 'thumbnail', inputType: 'custom', order: 3 },
      {
        name: 'dataBrowserSourceSheet',
        label: 'Data Browser Source Sheet',
        inputType: 'select',
        type: 'string',
        inputOptions: {
          order: 2,
          disabled: !enableDataBrowser,
          selectOptions: [
            allOption,
            ...(typesData || []).map((item) => ({
              label: item.DisplayName,
              value: item.Name,
            })),
          ],
        },
      },
      {
        name: 'schema',
        inputType: 'editor',
        label: 'Values Schema',
        inputOptions: {
          language: 'json',
          height: '420px',
        },
      },
    ];
  }, [typesData, enableDataBrowser]);

  const initialValues = useDeepCompareMemo(() => {
    return {
      enableDataBrowser: !!workspaceData?.Features?.enableDataBrowser,
      dataBrowserSourceSheet: workspaceData?.Features?.dataBrowserSourceSheet
        ? {
            value: workspaceData?.Features?.dataBrowserSourceSheet,
            label: workspaceData?.Features?.dataBrowserSourceSheet,
          }
        : allOption,
      schema: JSON.stringify(workspaceData?.ValuesSchema || {}, null, 2),
      thumbnail: workspaceData?.Features?.thumbnail,
    };
  }, [workspaceData]);

  const handleFormAction = async (data: any) => {
    try {
      await updateProjectTemplate({
        projectTemplateId: workspaceData.ID,
        data: {
          features: {
            ...data,
            dataBrowserSourceSheet:
              data?.dataBrowserSourceSheet?.value === allOption.value ? '' : data.dataBrowserSourceSheet.value,
          },
          values_schema: JSON.parse(data.schema),
        },
      }).unwrap();

      dispatch(
        addNotification({
          type: 'success',
          message: 'Workspace template settings has been successfully updated.',
        }),
      );
    } catch (error) {
      const message = `Error updating workspace template settings.`;
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );

      console.error(message, error);
    }
  };

  const handleBreadcrumbClick = (action: any) => {
    navigate(`/${action?.item.slug}`);
  };

  // auto submit after thumbnail change
  useDeepCompareMemo(() => {
    //@ts-ignore
    if (thumbnail?.ID && dirtyFields?.thumbnail && isValid) {
      handleSubmit(handleFormAction)();
    }
  }, [thumbnail?.ID, isDirty, isValid, dirtyFields]);

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <NgBreadcrumbs data={breadcrumbs} onClick={handleBreadcrumbClick} />
        </NgCardHeader>

        <br />

        <FolderManagerDynamicForm
          isLoading={isLoading}
          initialValues={initialValues}
          customFormMethods={customFormMethods}
          formSchema={{ fields: fields }}
          onAction={handleFormAction}
          customComponents={{
            thumbnail: (
              <Controller
                name="thumbnail"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <NgAssetUploadInput
                    asset={value}
                    uploadFolderId={workspaceData?.ThumbnailsFolderID}
                    acceptedFileTypes="image/*"
                    isLoading={isLoading}
                    helperText="Preffered image size is 248 x 154 px."
                    onUpload={({ responseData }) => {
                      onChange({
                        ID: responseData?.AssetVersion?.AssetID,
                        DisplayName: responseData?.FileName,
                      });
                    }}
                  />
                )}
              />
            ),
          }}
        />
      </NgCardContent>
    </NgCard>
  );
};
